import { useState } from "react";
import { UpdateProductBulkRequest } from "../../Models/Product";
import { LengthUnit } from "../../Enums/LengthUnit";
import { BulkUpdateField } from "../../Enums/BulkUpdateField";
import { WeightUnit } from "../../Enums/WeightUnit";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { getBulkUpdateFieldOptions } from "../../Utils/Enums";
import { parseCurrencyInput } from "../../Utils/InputParsers";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import SearchBar from "../SearchBar/SearchBar";
import MeasurementInput from "../MeasurementInput/MeasurementInput";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";

type BulkUpdateProductsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  categories: { id: string; name: string }[];
  brands: { id: string; name: string }[];
  onConfirm: (payload: UpdateProductBulkRequest) => Promise<number>;
};

const BulkUpdateProductsModal = ({
  isOpen,
  onClose,
  categories,
  brands,
  onConfirm
}: BulkUpdateProductsModalProps) => {
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [applyToAll, setApplyToAll] = useState(false);
  const [field, setField] = useState<BulkUpdateField>(BulkUpdateField.None);
  const [isActive, setIsActive] = useState("");
  const [isMadeToOrder, setIsMadeToOrder] = useState("");
  const [stockQuantity, setStockQuantity] = useState<number | null>(null);
  const [weightValue, setWeightValue] = useState<number | null>(null);
  const [weightUnit, setWeightUnit] = useState<WeightUnit | "">("");
  const [heightValue, setHeightValue] = useState<number | null>(null);
  const [heightUnit, setHeightUnit] = useState<LengthUnit | "">("");
  const [widthValue, setWidthValue] = useState<number | null>(null);
  const [widthUnit, setWidthUnit] = useState<LengthUnit | "">("");
  const [lengthValue, setLengthValue] = useState<number | null>(null);
  const [lengthUnit, setLengthUnit] = useState<LengthUnit | "">("");
  const [price, setPrice] = useState<number | null>(null);
  const [ncm, setNcm] = useState("");
  const [barcode, setBarcode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const resetFields = () => {
    setSearch("");
    setCategoryId("");
    setBrandId("");
    setApplyToAll(false);
    setField(BulkUpdateField.None);
    setIsActive("");
    setIsMadeToOrder("");
    setStockQuantity(null);
    setWeightValue(null);
    setWeightUnit("");
    setHeightValue(null);
    setHeightUnit("");
    setWidthValue(null);
    setWidthUnit("");
    setLengthValue(null);
    setLengthUnit("");
    setPrice(null);
    setNcm("");
    setBarcode("");
  };

  const handleMeasurementValueChange = (
    setter: React.Dispatch<React.SetStateAction<number | null>>,
    rawValue: string
  ) => {
    const trimmed = rawValue.trim();
    const nextValue = trimmed === "" ? null : Number(trimmed);

    if (trimmed !== "" && Number.isNaN(nextValue)) {
      return;
    }

    setter(nextValue);
  };

  const toggleWeightUnit = () => {
    setWeightUnit((current) => {
      const normalized = current === "" ? WeightUnit.KG : current;
      return normalized === WeightUnit.KG ? WeightUnit.G : WeightUnit.KG;
    });
  };

  const toggleLengthUnit = (
    setter: React.Dispatch<React.SetStateAction<LengthUnit | "">>
  ) => {
    setter((current) => {
      const normalized = current === "" ? LengthUnit.CM : current;
      return normalized === LengthUnit.CM
        ? LengthUnit.M
        : normalized === LengthUnit.M
          ? LengthUnit.MM
          : LengthUnit.CM;
    });
  };

  const getLengthUnitLabel = (unit: LengthUnit | "" | null | undefined) => {
    if (unit === LengthUnit.M) {
      return "m";
    }

    if (unit === LengthUnit.MM) {
      return "mm";
    }

    return "cm";
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!applyToAll && !search.trim() && !categoryId && !brandId) {
      toast.error("Informe um filtro por busca, categoria ou marca.");
      return;
    }

    if (field === BulkUpdateField.None) {
      toast.error("Selecione o atributo que deseja atualizar.");
      return;
    }

    const payload: UpdateProductBulkRequest = { field };

    payload.applyToAll = applyToAll;
    payload.field = field;

    if (!applyToAll && search.trim()) {
      payload.search = search.trim();
    }

    if (!applyToAll && categoryId) {
      payload.categoryId = categoryId;
    }

    if (!applyToAll && brandId) {
      payload.brandId = brandId;
    }

    switch (field) {
      case BulkUpdateField.IsActive:
        if (isActive === "") {
          toast.error("Selecione o status ativo.");
          return;
        }
        payload.isActive = isActive === "true";
        break;
      case BulkUpdateField.IsMadeToOrder:
        if (isMadeToOrder === "") {
          toast.error("Selecione se o produto é sob encomenda.");
          return;
        }
        payload.isMadeToOrder = isMadeToOrder === "true";
        break;
      case BulkUpdateField.StockQuantity:
        if (stockQuantity === null || Number.isNaN(stockQuantity)) {
          toast.error("Informe o estoque.");
          return;
        }
        if (stockQuantity < 0) {
          toast.error("Estoque inválido. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.stockQuantity = stockQuantity;
        break;
      case BulkUpdateField.Ncm: {
        const trimmed = ncm.trim();
        if (!trimmed) {
          toast.error("Informe o NCM do produto.");
          return;
        }
        if (!/^(\d{2}|\d{8})$/.test(trimmed)) {
          toast.error("NCM inválido. Use 2 ou 8 dígitos.");
          return;
        }
        payload.ncm = trimmed;
        break;
      }
      case BulkUpdateField.Barcode: {
        const trimmed = barcode.trim();
        if (trimmed && !/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(trimmed)) {
          toast.error("Código de barras inválido. Use 8, 12, 13 ou 14 dígitos ou deixe em branco.");
          return;
        }
        payload.barcode = trimmed;
        break;
      }
      case BulkUpdateField.Weight:
        if (weightValue !== null && weightValue < 0) {
          toast.error("Peso inválido. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.weightValue = weightValue;
        payload.weightUnit = weightValue == null ? null : (weightUnit !== "" ? weightUnit : WeightUnit.KG);
        break;
      case BulkUpdateField.Height:
        if (heightValue !== null && heightValue < 0) {
          toast.error("Altura inválida. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.heightValue = heightValue;
        payload.heightUnit = heightValue == null ? null : (heightUnit || LengthUnit.CM);
        break;
      case BulkUpdateField.Width:
        if (widthValue !== null && widthValue < 0) {
          toast.error("Largura inválida. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.widthValue = widthValue;
        payload.widthUnit = widthValue == null ? null : (widthUnit || LengthUnit.CM);
        break;
      case BulkUpdateField.Length:
        if (lengthValue !== null && lengthValue < 0) {
          toast.error("Comprimento inválido. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.lengthValue = lengthValue;
        payload.lengthUnit = lengthValue == null ? null : (lengthUnit || LengthUnit.CM);
        break;
      case BulkUpdateField.Price:
        if (price == null || Number.isNaN(price)) {
          toast.error("Informe o preço.");
          return;
        }
        if (price < 0) {
          toast.error("Preço inválido. Informe um valor maior ou igual a zero.");
          return;
        }
        payload.price = price;
        break;
    }

    setIsLoading(true);
    try {
      const updatedCount = await onConfirm(payload);
      if (updatedCount > 0) {
        toast.success(`Produtos atualizados: ${updatedCount}.`);
        resetFields();
        onClose();
      } else {
        toast.warn("Nenhum produto encontrado para atualização.");
      }
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Erro ao atualizar produtos.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-md shadow-xl px-6 py-8 relative w-full max-w-4xl max-h-[85vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 text-2xl font-bold ${
            isLoading ? "text-gray-400 cursor-not-allowed" : "text-primary-600 hover:text-primary-700"
          }`}
          disabled={isLoading}
        >
          <FaTimes />
        </button>

        <h3 className="text-lg font-bold text-primary-600 mb-4">{"Atualizar produtos em massa"}</h3>
        <p className="text-sm text-gray-600 mb-6">
          {"Atualize status, estoque, dimensões, NCM e código de barras em vários produtos ao mesmo tempo."}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="border border-gray-200 rounded-md p-4 space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">{"Filtros"}</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={"Aplicar a todos os produtos"}
              name="applyToAll"
              type="checkbox"
              value={applyToAll}
              onChange={(e) => setApplyToAll((e.target as HTMLInputElement).checked)}
              disabled={isLoading}
            />

            {applyToAll && (
              <p className="text-sm text-red-600 font-medium">
                {"Atenção: essa ação atualizará todos os produtos da loja."}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="block mb-1 text-gray-600 text-sm font-medium">
                {"Pesquisa"}
              </label>
              <SearchBar
                value={search}
                onSearch={setSearch}
                placeholder={"Nome ou código"}
                className="w-full h-[48px]"
                inputClassName="px-3 py-3 text-base text-gray-900 placeholder:text-gray-900"
                buttonClassName="px-3 py-3"
                borderWidthClass="border"
                borderClass="border-gray-300"
                iconClass="text-primary-600"
                disabled={applyToAll || isLoading}
              />
            </div>

            <Select
              label={"Categoria"}
              name="categoryId"
              value={categoryId}
              placeholder={"Selecione uma categoria"}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={applyToAll || isLoading}
              options={categories.map(c => ({ value: c.id, label: c.name }))}
            />

            <Select
              label={"Marca"}
              name="brandId"
              value={brandId}
              placeholder={"Selecione uma marca"}
              onChange={(e) => setBrandId(e.target.value)}
              disabled={applyToAll || isLoading}
              options={brands.map(b => ({ value: b.id, label: b.name }))}
            />
          </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4 space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">{"Campo para atualizar"}</h4>

            <Select
              label={"Atributo"}
              name="field"
              value={field === BulkUpdateField.None ? "" : field}
              placeholder={"Selecione o atributo"}
              onChange={(e) => setField(e.target.value === "" ? BulkUpdateField.None : (Number(e.target.value) as BulkUpdateField))}
              disabled={isLoading}
              options={getBulkUpdateFieldOptions()}
            />

            {field === BulkUpdateField.IsActive && (
              <Select
                label="Status"
                name="isActive"
                value={isActive}
                placeholder={"Selecione"}
                onChange={(e) => setIsActive(e.target.value)}
                disabled={isLoading}
                options={[
                  { value: "true", label: "Ativo" },
                  { value: "false", label: "Inativo" }
                ]}
              />
            )}

            {field === BulkUpdateField.IsMadeToOrder && (
              <Select
                label={"Sob encomenda"}
                name="isMadeToOrder"
                value={isMadeToOrder}
                placeholder={"Selecione"}
                onChange={(e) => setIsMadeToOrder(e.target.value)}
                disabled={isLoading}
                options={[
                  { value: "true", label: "Sim" },
                  { value: "false", label: "Não" }
                ]}
              />
            )}

            {field === BulkUpdateField.StockQuantity && (
              <Input
                label={"Estoque"}
                name="stockQuantity"
                type="number"
                min={0}
                value={stockQuantity ?? ""}
                onChange={(e) => setStockQuantity(e.target.value === "" ? null : Number(e.target.value))}
                disabled={isLoading}
              />
            )}

            {field === BulkUpdateField.Ncm && (
              <Input
                label="NCM"
                name="ncm"
              value={ncm}
              onChange={(e) => setNcm(e.target.value)}
              disabled={isLoading}
            />
          )}

            {field === BulkUpdateField.Barcode && (
              <Input
                label={"Código de barras"}
                name="barcode"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              disabled={isLoading}
            />
          )}

            {field === BulkUpdateField.Weight && (
              <MeasurementInput
                label={"Peso"}
                name="weightValue"
                value={weightValue ?? ""}
                unitLabel={weightUnit === WeightUnit.G ? "g" : "kg"}
                onValueChange={(value) => {
                  if (!isLoading) {
                    handleMeasurementValueChange(setWeightValue, value);
                  }
                }}
                onUnitToggle={() => {
                  if (!isLoading) {
                    toggleWeightUnit();
                  }
                }}
                step={0.01}
                min={0}
              />
            )}

            {field === BulkUpdateField.Height && (
              <MeasurementInput
                label={"Altura"}
                name="heightValue"
                value={heightValue ?? ""}
                unitLabel={getLengthUnitLabel(heightUnit)}
                onValueChange={(value) => {
                  if (!isLoading) {
                    handleMeasurementValueChange(setHeightValue, value);
                  }
                }}
                onUnitToggle={() => {
                  if (!isLoading) {
                    toggleLengthUnit(setHeightUnit);
                  }
                }}
                step={0.01}
                min={0}
              />
            )}

            {field === BulkUpdateField.Width && (
              <MeasurementInput
                label={"Largura"}
                name="widthValue"
                value={widthValue ?? ""}
                unitLabel={getLengthUnitLabel(widthUnit)}
                onValueChange={(value) => {
                  if (!isLoading) {
                    handleMeasurementValueChange(setWidthValue, value);
                  }
                }}
                onUnitToggle={() => {
                  if (!isLoading) {
                    toggleLengthUnit(setWidthUnit);
                  }
                }}
                step={0.01}
                min={0}
              />
            )}

            {field === BulkUpdateField.Length && (
              <MeasurementInput
                label={"Comprimento"}
                name="lengthValue"
                value={lengthValue ?? ""}
                unitLabel={getLengthUnitLabel(lengthUnit)}
                onValueChange={(value) => {
                  if (!isLoading) {
                    handleMeasurementValueChange(setLengthValue, value);
                  }
                }}
                onUnitToggle={() => {
                  if (!isLoading) {
                    toggleLengthUnit(setLengthUnit);
                  }
                }}
                step={0.01}
                min={0}
              />
            )}

            {field === BulkUpdateField.Price && (
              <Input
                label={"Preço"}
                type="text"
                name="price"
                value={formatPriceBRL(price ?? null) || ""}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  if (!rawValue.trim()) {
                    setPrice(null);
                    return;
                  }
                  setPrice(parseCurrencyInput(rawValue));
                }}
                required
                disabled={isLoading}
              />
            )}
          </div>

          <Button isLoading={isLoading}
            type="submit"
            className="py-2"
            disabled={isLoading}
          >
            {"APLICAR ALTERAÇÃO"}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default BulkUpdateProductsModal;
